import styled from '@emotion/styled';
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit'; // Import EditIcon
import mainContext from '../../../contexts/mainContext';
import { addBabySitterChildrenExperienceController, deleteData, getBabySitterChildrenExperienceController } from '../../../Controllers/AppManagement/babySitter';
import { __constants, __snackBarText, __snackBarSeverity } from '../../../constants';

function FormDialog({ open, handleClose, handleAdd, editData }) {
    const [sno, setSno] = React.useState("");

    React.useEffect(() => {
        if (editData) {
            setSno(editData.sno || "");
        } else {
            setSno("");
        }
    }, [editData, open]);

    return (
        <>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{editData ? "Edit children range" : "Add new children range"}</DialogTitle>
                <DialogContent>
                    <strong style={{
                        display: "block"
                    }}>Attach active file</strong>
                    <input accept='image/png' type="file" id='attachedFile' />
                    <br />
                    <br />
                    <strong style={{
                        display: "block"
                    }}>Attach inactive file</strong>
                    <input accept='image/png' type="file" id='greyImage' />
                    <TextField
                        autoFocus
                        margin="dense"
                        id="content"
                        label="Children experience"
                        type="text"
                        fullWidth
                        variant="standard"
                        inputProps={{maxLength:50}}
                        defaultValue={editData ? editData.name : ""}
                    />
                    <TextField
                        margin="dense"
                        id="sno"
                        label="Serial Number (1-1000)"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={sno}
                        helperText="Used for sorting and mobile display order"
                        FormHelperTextProps={{ style: { color: "#999" } }}
                        onChange={(e) => {
                            const reg = new RegExp(/^[0-9]*$/gi)
                            const res = reg.test(e.target.value.replace(/\n/g, " "))
                            if (res) {
                                setSno(e.target.value)
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} size="small">Cancel</Button>
                    <Button onClick={() => handleAdd(sno)} variant="contained" size="small">
                        {editData ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const BabySitterChildren = () => {
    const { setSnackBar, setProfileNavTitle } = React.useContext(mainContext);
    const [babySitterChildrenExperience, setBabySitterChildrenExperience] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [imgChanged, setImgChanged] = React.useState(false);
    const [editData, setEditData] = React.useState(null);
    const [loading, setLoading] = useState(false);

    const callSnackBar = (msg, type) => {
        setSnackBar({
            status: true,
            text: msg,
            severity: __snackBarSeverity[type]
        });
    }

    const handleClickOpen = () => {
        setEditData(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditData(null);
    };

    const handleEditOpen = (item) => {
        setEditData(item);
        setOpen(true);
    };

    const handleAdd = async (sno) => {
        const name = document.getElementById("content").value;
        const file = document.getElementById("attachedFile");
        const greyImage = document.getElementById("greyImage");
        
        if (name === "") {
            return callSnackBar(__snackBarText.fieldEror, "warning");
        }

        if (sno === "") {
            return callSnackBar("Please enter serial number", "warning");
        }

        if (Number.parseInt(sno) < 1 || Number.parseInt(sno) > 1000) {
            return callSnackBar("Serial number must be between 1 and 1000", "error");
        }

        // Check if we're in edit mode and we're not changing the files
        if (editData) {
            // For edit mode, handle file validation differently
            if ((file.files.length > 0 || greyImage.files.length > 0)) {
                // If any files are provided, validate both
                if (file.files.length === 0 || greyImage.files.length === 0) {
                    return callSnackBar("Please add both images", "warning");
                }
                
                if (file.files.length > 0) {
                    var idxDot = file.files[0].name.lastIndexOf(".") + 1;
                    var extFile = file.files[0].name.substr(idxDot, file.files[0].name.length).toLowerCase();
                    if (extFile !== "png") {
                        return alert("Only png files are allowed!");
                    }
                }
                
                if (greyImage.files.length > 0) {
                    idxDot = greyImage.files[0].name.lastIndexOf(".") + 1;
                    extFile = greyImage.files[0].name.substr(idxDot, greyImage.files[0].name.length).toLowerCase();
                    if (extFile !== "png") {
                        return alert("Only png files are allowed!");
                    }
                }
            }
            
            try {
                // Call the API for update
                // Here we need to use the appropriate controller
                // Since we don't have a specific update controller visible in the imports,
                // we can use the same addBabySitterChildrenExperienceController with the ID
                const res = await addBabySitterChildrenExperienceController(
                    name,
                    file.files.length > 0 ? file.files[0] : null,
                    greyImage.files.length > 0 ? greyImage.files[0] : null,
                    sno,
                    editData._id  // Pass the ID for update
                );
                
                if (res && res.status === 500) {
                    return callSnackBar(__snackBarText.internalServerError, "error");
                }
                
                if (res.data.status !== 200) {
                    return callSnackBar(res.data.errors ? res.data.errors[0].message : res.data.message, "error");
                }
                
                // Refresh data from server after update
                await getBabySitterChildrenExperience();
                
                callSnackBar("Data updated successfully", "success");
                handleClose();
                return;
            } catch (error) {
                console.error("Update error:", error);
                callSnackBar("Error updating data", "error");
                return;
            }
        }
        
        // For new entries, validate both files are present
        if (file.files.length === 0 || greyImage.files.length === 0) {
            return callSnackBar("Please add both images", "warning");
        }
          
        var idxDot = file.files[0].name.lastIndexOf(".") + 1;
        var extFile = file.files[0].name.substr(idxDot, file.files[0].name.length).toLowerCase();
        if (extFile !== "png") {
            return alert("Only png files are allowed!");
        }
          
        idxDot = greyImage.files[0].name.lastIndexOf(".") + 1;
        extFile = greyImage.files[0].name.substr(idxDot, greyImage.files[0].name.length).toLowerCase();
        if (extFile !== "png") {
            return alert("Only png files are allowed!");
        }
        
        try {
            // Add new entry with sno
            const res = await addBabySitterChildrenExperienceController(name, file.files[0], greyImage.files[0], sno);
            
            if (res && res.status === 500) {
                return callSnackBar(__snackBarText.internalServerError, "error");
            }
            
            if (res.data.status !== 200) {
                return callSnackBar(res.data.errors ? res.data.errors[0].message : res.data.message, "error");
            }
            
            // Refresh data from server after adding
            await getBabySitterChildrenExperience();
            
            callSnackBar("Data added successfully", "success");
            file.value = null;
            greyImage.value = null;
            handleClose();
        } catch (error) {
            console.error("Add error:", error);
            callSnackBar("Error adding data", "error");
        }
    }

    const getBabySitterChildrenExperience = async () => {
        setLoading(true);
        try {
            const res = await getBabySitterChildrenExperienceController();
            if (res && res.status === 500) {
                callSnackBar(__snackBarText.internalServerError, "error");
                return;
            }
            if (res.data.status !== 200) {
                callSnackBar(res.data.errors[0].message, "error");
                return;
            }
            if (res.data.data.data.length === 0) {
                callSnackBar("No data found", "info");
            }
            setBabySitterChildrenExperience(res.data.data.data);
        } catch (error) {
            callSnackBar("Error loading children experience data", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = async (id) => {
        try {
            const res = await deleteData(id);
            if (res && res.status === 500) {
                return callSnackBar(__snackBarText.internalServerError, "error");
            }
            if (res.data.status !== 200) {
                return callSnackBar(res.data.errors[0].message, "error");
            }

            // Refresh data from server after delete
            await getBabySitterChildrenExperience();
            
            callSnackBar("Data deleted successfully", "success");
        } catch (error) {
            callSnackBar("Error deleting data", "error");
        }
    }

    React.useEffect(() => {
        getBabySitterChildrenExperience();
        setProfileNavTitle("Babysitter children experience");
        // eslint-disable-next-line
    }, []);
    
    return (
        <>
            <FormDialog open={open} handleClose={handleClose} handleAdd={handleAdd} editData={editData} />
            <Conatiner>
                <Header>
                    <Button variant="contained" size='small' onClick={handleClickOpen}>Add new</Button>
                </Header>
                <Body>
                    {loading ? (
                        <div className="loading-container">
                            <p>Loading...</p>
                        </div>
                    ) : (
                        <>
                            <List>
                                <div className="header">S.No</div>
                                <div className="header">Active Image</div>
                                <div className="header">Inactive image</div>
                                <div className="header">Children experience</div>
                                <div></div>
                                <div></div>
                            </List>
                            <Divider className='divider-gap' />
                            {
                                babySitterChildrenExperience.map((item, index) => {
                                    return (
                                        <List key={item._id}>
                                            <div>
                                                {item.sno || "-"}
                                            </div>
                                            <div>
                                                <ImgDiv>
                                                    <img src={`${__constants.images}${item.file}?${Date.now()}`} alt="" />
                                                </ImgDiv>
                                            </div>
                                            <div>
                                                <ImgDiv>
                                                    <img src={`${__constants.images}${item.greyImage}?${Date.now()}`} alt="" />
                                                </ImgDiv>
                                            </div>
                                            <div>
                                                {item.name}
                                            </div>
                                            <div>
                                                <IconButton onClick={() => handleEditOpen(item)}>
                                                    <EditIcon color="primary" fontSize="small" />
                                                </IconButton>
                                            </div>
                                            <div>
                                                <Button size="small" color="error" onClick={() => { handleDelete(item._id) }}>Delete</Button>
                                            </div>
                                        </List>
                                    )
                                })
                            }
                        </>
                    )}
                </Body>
            </Conatiner>
        </>
    )
}

const Conatiner = styled("div")`
  .divider-gap{
        margin: 0.5rem 0;
  }
  button{
    text-transform: none;
  }
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
`;

const Header = styled("div")`
  button{
    border-radius: 5px;
  }
`;
const Body = styled("div")`
  height: 80vh;
`;

const List = styled("div")`
.header{
    font-family: "semi-bold";
    font-size : 90%
}
  margin-top: 1rem;
  display: flex;
  align-items: center;
  &>div:nth-child(1){
    width: 5%;
  }
  &>div:nth-child(2){
    width: 20%;
  }
  &>div:nth-child(3){
    width: 20%;
  }
  &>div:nth-child(4){
    width: 40%;
  }
  &>div:nth-child(5){
    width: 5%;
  }
  &>div:nth-child(6){
    width: 10%;
  }
`;

const ImgDiv = styled("div")`
  height: 2rem;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &>img{
    width: 100%;
    object-fit: contain;
    clip-path: circle();
  }
`;

// Custom styled IconButton component
const IconButton = styled("div")`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`
export default BabySitterChildren